import React, { useState, useEffect } from 'react';
import { 
  FaSearch, 
  FaSort, 
  FaSortAlphaDown, 
  FaSortAlphaUp, 
  FaSortAmountDown, 
  FaSortAmountUp,
  FaTrash,
  FaDownload,
  FaFilter,
  FaSignOutAlt,
  FaTag,
  FaFolderPlus
} from 'react-icons/fa';

const Toolbar = ({ 
  searchTerm,
  onSearchChange,
  sortBy,
  sortOrder,
  onSortChange,
  viewMode,
  onViewModeChange,
  selectedFiles,
  onCreateFolder,
  onUploadFiles,
  onDeleteSelected,
  onDownloadSelected,
  onTagSelected,
  onLogout,
  selectedTag,
  onClearTag,
  tagResultsLoading = false,
  tagResultsError = null,
  selectedFolderTag = null,
  onClearFolderTag = null,
  folderTagResultsLoading = false,
  folderTagResultsError = null,
  onCreateTempFolder,
  onAddToTempFolder,
  tempFolderFilesCount = 0
}) => {
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [localSearch, setLocalSearch] = useState(searchTerm || '');

  const sortOptions = [
    { value: 'name', label: 'Nombre', icon: FaSortAlphaDown },
    { value: 'size', label: 'Tamaño', icon: FaSortAmountDown },
    { value: 'date', label: 'Fecha', icon: FaSort },
    { value: 'type', label: 'Tipo', icon: FaFilter }
  ];

  const getSortIcon = () => {
    if (sortBy === 'name') {
      return sortOrder === 'asc' ? FaSortAlphaDown : FaSortAlphaUp;
    }
    return sortOrder === 'asc' ? FaSortAmountDown : FaSortAmountUp;
  };

  const SortIcon = getSortIcon();

  const handleInputChange = (e) => {
    setLocalSearch(e.target.value);
  };

  const handleSearch = () => {
    onSearchChange(localSearch);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      onSearchChange(localSearch);
    }
  };

  useEffect(() => {
    setLocalSearch(searchTerm || '');
  }, [searchTerm]);

  return (
    <div className="bg-toolbar-bg border-b border-toolbar-border p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4 flex-1">
          <div className="relative max-w-md flex-1">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Busca archivos o carpetas"
              value={localSearch}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              className="w-full pl-10 pr-16 py-2 bg-white border border-gray-300 rounded-lg text-toolbar-text placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
            <button
              onClick={handleSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-800 focus:outline-none"
              aria-label="Buscar"
            >
              <FaSearch />
            </button>
          </div>
          {selectedFolderTag && (
            <div className="flex items-center space-x-2 bg-yellow-600/80 text-white px-3 py-1 rounded-full">
              <span className="text-sm font-medium truncate max-w-[140px]">
                Tag: #{selectedFolderTag}
              </span>
              <button
                onClick={onClearFolderTag}
                className="text-white/80 hover:text-white focus:outline-none"
                title="Quitar filtro por tag de carpeta"
              >
                ×
              </button>
            </div>
          )}
          {selectedTag && (
            <div className="flex items-center space-x-2 bg-blue-600/80 text-white px-3 py-1 rounded-full">
              <span className="text-sm font-medium truncate max-w-[140px]">
                Tag: #{selectedTag}
              </span>
              <button
                onClick={onClearTag}
                className="text-white/80 hover:text-white focus:outline-none"
                title="Quitar filtro por tag"
              >
                ×
              </button>
            </div>
          )}
          {folderTagResultsLoading && (
            <div className="flex items-center space-x-2 text-xs text-yellow-200">
              <div className="w-3 h-3 border border-yellow-200 border-t-transparent rounded-full animate-spin"></div>
              <span>Cargando carpetas del tag...</span>
            </div>
          )}
          {tagResultsLoading && (
            <div className="flex items-center space-x-2 text-xs text-blue-200">
              <div className="w-3 h-3 border border-blue-200 border-t-transparent rounded-full animate-spin"></div>
              <span>Cargando archivos del tag...</span>
            </div>
          )}
          {folderTagResultsError && (
            <div className="text-xs text-red-300 max-w-xs truncate">
              {folderTagResultsError}
            </div>
          )}
          {tagResultsError && (
            <div className="text-xs text-red-300 max-w-xs truncate">
              {tagResultsError}
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <div className="relative">
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              className="flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-toolbar-text transition-colors"
            >
              <SortIcon className="text-sm" />
              <span className="text-sm">Ordenar</span>
            </button>

            {showSortMenu && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-300 rounded-lg shadow-lg z-10">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      onSortChange(option.value, sortBy === option.value && sortOrder === 'asc' ? 'desc' : 'asc');
                      setShowSortMenu(false);
                    }}
                    className={`
                      w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-gray-100 transition-colors
                      ${sortBy === option.value ? 'bg-gray-100 text-blue-600' : 'text-toolbar-text'}
                    `}
                  >
                    <option.icon className="text-sm" />
                    <span>{option.label}</span>
                    {sortBy === option.value && (
                      <span className="ml-auto text-xs">
                        {sortOrder === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Botón de crear carpeta temporal */}
          <button
            onClick={onCreateTempFolder}
            disabled={tempFolderFilesCount === 0}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white transition-colors shadow-sm relative disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-600"
            title={tempFolderFilesCount === 0 ? "Agrega archivos a la carpeta temporal primero" : "Crear carpeta temporal para compartir"}
          >
            <FaFolderPlus className="text-sm" />
            <span>Crear carpeta temporal</span>
            {tempFolderFilesCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg">
                {tempFolderFilesCount}
              </span>
            )}
          </button>

          {/* Botón de cerrar sesión */}
          {onLogout && (
            <button
              onClick={onLogout}
              className="flex items-center space-x-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              title="Cerrar sesión"
            >
              <FaSignOutAlt className="text-sm" />
              <span className="text-sm font-medium">Cerrar sesión</span>
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        {/* Botones principales a la izquierda */}
        <div className="flex items-center space-x-3">
          {/* El botón de crear carpeta temporal se movió arriba */}
        </div>

        {/* Espaciador para empujar contenido a la derecha */}
        <div></div>

        {/* Botones de archivos seleccionados - movidos a la derecha */}
        {selectedFiles.length > 0 && (
          <div className="flex items-center space-x-3">
            <span className="text-sm text-toolbar-text font-medium">
              {selectedFiles.length} seleccionados
            </span>
            
            <button
              onClick={onAddToTempFolder}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white transition-colors shadow-sm"
              title="Agregar seleccionados a carpeta temporal"
            >
              <FaFolderPlus className="text-sm" />
              <span>Agregar a carpeta temporal</span>
            </button>
            
            <button
              onClick={onTagSelected}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white transition-colors shadow-sm"
              title="Etiquetar seleccionados"
            >
              <FaTag className="text-sm" />
              <span>Etiquetar</span>
            </button>
            
            <button
              onClick={onDownloadSelected}
              className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 rounded-lg text-white transition-colors shadow-sm"
              title="Descargar seleccionados"
            >
              <FaDownload className="text-sm" />
              <span>Descargar</span>
            </button>
            
            <button
              onClick={onDeleteSelected}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white transition-colors shadow-sm"
              title="Eliminar seleccionados"
            >
              <FaTrash className="text-sm" />
              <span>Eliminar</span>
            </button>
          </div>
        )}
      </div>

      {showSortMenu && (
        <div 
          className="fixed inset-0 z-5" 
          onClick={() => setShowSortMenu(false)}
        />
      )}
    </div>
  );
};

export default Toolbar; 